import { assert, isDeepEqual, isNullish } from "@bodil/core/assert";
import { DisposableContext, toDisposable } from "@bodil/core/disposable";
import { attribute, Component, type Emits } from "@bodil/dom/component";
import { getComputedProperty } from "@bodil/dom/css";
import { animationEnd } from "@bodil/dom/dom";
import {
    autoUpdate,
    computePosition,
    flip,
    offset,
    shift,
    size,
    type ComputePositionConfig,
    type ComputePositionReturn,
    type OffsetOptions,
    type Placement,
    type ReferenceElement,
} from "@floating-ui/dom";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { findMap } from "./lib";

import styles from "./popup.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-popup": BtkPopup;
    }

    interface HTMLElementEventMap {
        /**
         * A popup has changed position.
         * @event
         */
        "btk-popup-reposition": CustomEvent<ComputePositionReturn>;
        /**
         * A popup has opened.
         * @event
         */
        "btk-popup-opened": Event;
        /**
         * A popup has closed.
         * @event
         */
        "btk-popup-closed": Event;
        /**
         * Something wants a parent popup to open.
         * @event
         */
        "btk-popup-show": Event;
        /**
         * Something wants a parent popup to close.
         * @event
         */
        "btk-popup-hide": Event;
        /**
         * Something wants a parent popup to toggle.
         * @event
         */
        "btk-popup-toggle": Event;
    }
}

@customElement("btk-popup")
export class BtkPopup extends Component {
    emits!: Emits<"toggle" | "btk-popup-reposition" | "btk-popup-opened" | "btk-popup-closed">;

    @attribute accessor mode: "auto" | "manual" = "auto";
    @attribute accessor placement: Placement = "bottom-start";
    @attribute({ type: Number }) accessor distance: number | null = null;
    @attribute({ type: Number }) accessor skidding: number | null = null;
    @attribute accessor sync: "width" | "none" | null = null;
    @attribute({ type: Boolean }) accessor shift = false;
    @attribute({ type: Number }) accessor shiftPadding = 0;
    @attribute({ type: Boolean }) accessor flip = false;
    // If the anchor moves, should we hide the popup or move it with the anchor?
    @attribute accessor anchorAttach: "hide" | "follow" = "hide";

    set anchor(anchor: ReferenceElement | undefined) {
        this.#anchor = anchor;
        this.requestUpdate();
        void this.reposition();
    }
    get anchor(): ReferenceElement | undefined {
        return this.#anchor ?? this.anchorElements[0];
    }
    #anchor?: ReferenceElement;

    get isOpen(): boolean {
        if (isNullish(this.popup)) {
            return false;
        }
        return this.popup.value?.matches(":popover-open") ?? false;
    }

    private get anchorElements(): Array<HTMLElement> {
        return this.querySlot({ slot: "anchor", flatten: true, instanceOf: HTMLElement });
    }
    private get popupElements(): Array<HTMLElement> {
        return this.querySlot({ flatten: true, instanceOf: HTMLElement });
    }

    private readonly popup = createRef<HTMLDivElement>();

    autoUpdateContext = new DisposableContext();

    async hide(): Promise<void> {
        if (!this.isOpen) {
            return;
        }
        await this.updateComplete;
        assert(this.popup.value !== undefined);
        this.popup.value.hidePopover();
        await animationEnd(this.popup.value);
    }

    async show(): Promise<void> {
        if (this.isOpen) {
            return;
        }
        await this.updateComplete;
        assert(this.popup.value !== undefined);
        this.popup.value.showPopover();
        await animationEnd(this.popup.value);
    }

    async toggle(): Promise<void> {
        await this.updateComplete;
        assert(this.popup.value !== undefined);
        this.popup.value.togglePopover();
        await animationEnd(this.popup.value);
    }

    getPopupRect(): DOMRect | undefined {
        return this.isOpen ? this.popup.value?.getBoundingClientRect() : undefined;
    }

    protected override connected() {
        super.connected();
        if (this.isOpen) {
            this.startAutoUpdate();
        }
        this.useWhileConnected(
            this.on("btk-popup-show", () => {
                void this.show();
            }),
            this.on("btk-popup-hide", () => {
                void this.hide();
            }),
            this.on("btk-popup-toggle", () => {
                void this.toggle();
            }),
            () => this.stopAutoUpdate(),
        );
    }

    protected override updated() {
        super.updated();
        void this.reposition();
    }

    private startAutoUpdate() {
        this.autoUpdateContext.dispose();
        assert(this.popup.value !== undefined);
        if (this.anchor instanceof Node) {
            switch (this.anchorAttach) {
                case "follow": {
                    this.autoUpdateContext.use(
                        toDisposable(
                            autoUpdate(this.anchor, this.popup.value, () => void this.reposition()),
                        ),
                    );
                    break;
                }
                case "hide": {
                    let lastValue: ComputePositionReturn | undefined = undefined;
                    this.autoUpdateContext.use(
                        toDisposable(
                            autoUpdate(this.anchor, this.popup.value, () => {
                                void this.reposition(false).then((newValue) => {
                                    if (
                                        lastValue !== undefined &&
                                        !isDeepEqual(lastValue, newValue)
                                    ) {
                                        void this.hide();
                                    }
                                    lastValue = newValue;
                                });
                            }),
                        ),
                    );
                    break;
                }
            }
        }
    }

    private stopAutoUpdate() {
        this.autoUpdateContext.dispose();
    }

    private async reposition(apply = true): Promise<ComputePositionReturn | undefined> {
        if (
            !this.isConnected ||
            isNullish(this.popup.value) ||
            isNullish(this.anchor) ||
            isNullish(this.shadowRoot?.querySelector(":popover-open"))
        ) {
            return;
        }

        const config: ComputePositionConfig = {
            placement: this.placement,
            strategy: "fixed",
        };

        if (this.sync !== null && this.sync !== "none") {
            config.middleware = [
                ...(config.middleware ?? []),
                size({
                    apply: ({ rects }) => {
                        if (apply && this.sync === "width") {
                            this.popup.value!.style.width = `${rects.reference.width}px`;
                        }
                    },
                }),
            ];
        } else if (apply) {
            this.popup.value.style.width = "";
        }

        if (this.distance !== null || this.skidding !== null) {
            const offsetConfig: OffsetOptions = {};
            if (this.distance !== null) {
                offsetConfig.mainAxis = this.distance;
            }
            if (this.skidding !== null) {
                offsetConfig.alignmentAxis = this.skidding;
            }
            config.middleware = [...(config.middleware ?? []), offset(offsetConfig)];
        }

        if (this.shift) {
            config.middleware = [
                ...(config.middleware ?? []),
                shift({ padding: this.shiftPadding }),
            ];
        }

        if (this.flip) {
            config.middleware = [...(config.middleware ?? []), flip()];
        }

        let anchor: ReferenceElement | null = this.anchor;
        if (
            anchor instanceof HTMLElement &&
            getComputedProperty(anchor, "display").unwrap() === "contents"
        ) {
            if (anchor.shadowRoot !== null) {
                anchor = anchor.shadowRoot.firstElementChild;
            } else {
                anchor = anchor.firstElementChild;
            }
        }
        if (anchor === null) {
            // eslint-disable-next-line no-console
            console.warn("No anchor element found when repositioning popup", this);
            return;
        }

        const result = await computePosition(anchor, this.popup.value, config);

        if (apply) {
            const style: Record<string, string> = { left: `${result.x}px`, top: `${result.y}px` };
            Object.assign(this.popup.value.style, style);
            for (const element of [this, ...this.popupElements]) {
                const placement = result.placement.split("-")[0];
                const oldPlacement = element.getAttribute("data-popup-rendered-placement");
                if (placement !== oldPlacement) {
                    element.setAttribute("data-popup-rendered-placement", placement);
                    this.requestUpdate();
                }
            }
            this.emit("btk-popup-reposition", result);
        }

        return result;
    }

    private async handleToggleEvent(event: ToggleEvent) {
        if (event.oldState === "closed" && event.newState === "open") {
            await this.reposition();
            this.startAutoUpdate();
            this.emit("btk-popup-opened");
            this.focusFirstCandidate();
        } else if (event.oldState === "open" && event.newState === "closed") {
            this.stopAutoUpdate();
            this.emit("btk-popup-closed");
            this.blur();
        }
    }

    focusFirstCandidate(): boolean {
        const target =
            findMap(
                Iterator.from(this.popupElements),
                (el) => el.querySelector("[autofocus]") ?? undefined,
            ) ??
            findMap(
                Iterator.from(this.popupElements),
                (el) => el.querySelector(`[tabindex="0"]`) ?? undefined,
            );
        if (target instanceof HTMLElement) {
            target.focus();
            return true;
        }
        return false;
    }

    protected override render(): unknown {
        return html`<slot name="anchor" popovertarget="popover"></slot>
            <div
                ${ref(this.popup)}
                part="popover"
                popover=${this.mode}
                id="popover"
                @toggle=${this.handleToggleEvent}
            >
                <slot></slot>
            </div>`;
    }

    static styles = styles;
}
